import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { testRunExtraction } from "@/services/email-to-task.service";
import type { EmailDraft, InboxDirectory } from "@/services/inbound-email.service";
import { DraftTree } from "@/components/email-inbox/DraftTree";
import { Field, textareaClass } from "./fields";

/** Paste an email, run the saved agent, see the draft. Nothing is stored. */
export function TestRunDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
	const [subject, setSubject] = useState("");
	const [text, setText] = useState("");
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<{ draft: EmailDraft; directory: InboxDirectory } | null>(null);

	async function handleRun() {
		setRunning(true);
		setResult(null);
		try {
			setResult(await testRunExtraction({ text, subject: subject || undefined }));
		} catch (err) {
			toast.error((err as Error).message || "Test run failed.");
		} finally {
			setRunning(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle>Test with a sample email</DialogTitle>
					<DialogDescription>
						Runs the saved agent and rules on the text below. Nothing is stored and no tasks are created.
					</DialogDescription>
				</DialogHeader>

				<div className="mt-4 space-y-3">
					<Field label="Subject (optional)" htmlFor="ett-test-subject">
						<Input id="ett-test-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={running} />
					</Field>
					<Field label="Email body" htmlFor="ett-test-body">
						<textarea
							id="ett-test-body"
							rows={8}
							className={textareaClass}
							placeholder="Paste the email text here"
							value={text}
							onChange={(e) => setText(e.target.value)}
							disabled={running}
						/>
					</Field>
					<div className="flex items-center justify-end gap-3">
						{running && <p className="mr-auto text-xs text-muted">The agent can take up to a minute.</p>}
						<Button size="sm" onClick={handleRun} disabled={running || !text.trim()}>
							{running ? "Running..." : "Run test"}
						</Button>
					</div>
				</div>

				{result && (
					<div className="mt-6 max-h-[60vh] overflow-y-auto border-t border-border pt-4">
						<DraftTree draft={result.draft} directory={result.directory} editable={false} />
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
